/**
 * shared/contexts/AuthContext.js
 *
 * Shared AuthContext used by both the customer app and the admin app.
 * Identical logic to src/context/AuthContext.js — only the import paths change.
 * The admin app only uses: user, loading, isPlatformAdmin, refreshToken, logout.
 * All other exports are there for the customer app's backward compatibility.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, googleProvider, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import {
  onIdTokenChanged,
  signOut,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  sendEmailVerification,
} from "firebase/auth";

export const AuthContext = createContext();

function normalizeShopProfile(data) {
  if (!data) return data;
  return {
    ...data,
    shopName: data.shopName || data.storeName || data.businessName || "",
    ownerName: data.ownerName || data.proprietorName || data.contactName || data.name || "",
    phone: data.phone || data.mobile || data.phoneNumber || "",
    address: data.address || data.shopAddress || data.storeAddress || "",
    gstin: data.gstin || data.gst || data.gstNumber || "",
    status: data.status || data.approvalStatus || (data.approved === true || data.orderingApproved === true ? "approved" : "pending"),
  };
}

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [isAdmin, setIsAdmin]         = useState(null);
  const [shopProfile, setShopProfile] = useState(undefined);

  const [wholesalerId, setWholesalerId] = useState(null);
  const [userRole, setUserRole]         = useState(null);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (u) => {
      setUser(u);

      try {
        if (u) {
          const idTokenResult = await u.getIdTokenResult(true);

          setIsAdmin(
            idTokenResult.claims.role === "wholesaler_admin" ||
            idTokenResult.claims.role === "platform_admin"
          );

          setWholesalerId(idTokenResult.claims.wholesalerId || null);
          setUserRole(idTokenResult.claims.role || null);
        } else {
          setIsAdmin(false);
          setWholesalerId(null);
          setUserRole(null);
        }
      } catch (err) {
        console.error("Auth token hydration error:", err);
        setIsAdmin(false);
        setWholesalerId(null);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user || isAdmin === null) {
      if (!user) setShopProfile(null);
      return;
    }

    if (userRole === "platform_admin") {
      setShopProfile(null);
      return;
    }

    let cancelled = false;
    let unsub = null;

    getDoc(doc(db, "shopProfiles", user.uid))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? normalizeShopProfile(snap.data()) : null;
        setShopProfile(data);

        if (snap.exists() && !cancelled) {
          unsub = onSnapshot(
            doc(db, "shopProfiles", user.uid),
            (liveSnap) => {
              if (!cancelled) {
                setShopProfile(liveSnap.exists() ? normalizeShopProfile(liveSnap.data()) : null);
              }
            },
            (err) => console.error("shopProfile live error:", err)
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("shopProfile fetch error:", err);
          setShopProfile(null);
        }
      });

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [user, isAdmin, userRole]);

  const isLoggedIn = !loading && !!user;

  const isEmailPasswordAccount =
    !!user && user.providerData?.some((p) => p.providerId === "password");
  const requiresEmailVerification = isEmailPasswordAccount && !user?.emailVerified;

  const isPlatformAdmin = userRole === "platform_admin";

  const refreshToken = useCallback(async () => {
    const currentUser = auth.currentUser || user;
    if (!currentUser) return null;
    try {
      const idTokenResult = await currentUser.getIdTokenResult(true);
      setWholesalerId(idTokenResult.claims.wholesalerId || null);
      setUserRole(idTokenResult.claims.role || null);
      setIsAdmin(
        idTokenResult.claims.role === "wholesaler_admin" ||
        idTokenResult.claims.role === "platform_admin"
      );
      return idTokenResult;
    } catch (err) {
      console.error("Token refresh error:", err);
      throw err;
    }
  }, [user]);

  const getAuthToken = useCallback(async (forceRefresh = false) => {
    const currentUser = auth.currentUser || user;
    if (!currentUser) {
      throw new Error("Authentication required");
    }

    const token = await currentUser.getIdToken(forceRefresh);

    if (forceRefresh) {
      try {
        const idTokenResult = await currentUser.getIdTokenResult(false);
        setWholesalerId(idTokenResult.claims.wholesalerId || null);
        setUserRole(idTokenResult.claims.role || null);
        setIsAdmin(
          idTokenResult.claims.role === "wholesaler_admin" ||
          idTokenResult.claims.role === "platform_admin"
        );
      } catch (err) {
        console.error("Auth claim sync error:", err);
      }
    }

    return token;
  }, [user]);

  const loginWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error("Google Login Error:", err);
      throw err;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error("Email Login Error:", err);
      throw err;
    }
  };

  const signupWithEmail = async (email, password) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      try {
        await sendEmailVerification(cred.user);
      } catch (verifyErr) {
        console.error("sendEmailVerification error:", verifyErr);
      }
      return cred;
    } catch (err) {
      console.error("Signup Error:", err);
      throw err;
    }
  };

  const sendOTP = async (phoneNumber, recaptchaVerifier) => {
    try {
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      return confirmation;
    } catch (err) {
      console.error("OTP Error:", err);
      throw err;
    }
  };

  const verifyOTP = async (confirmation, code) => {
    try {
      await confirmation.confirm(code);
    } catch (err) {
      console.error("OTP Verify Error:", err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authLoading: loading,
        isLoggedIn,
        isAdmin,
        isPlatformAdmin,
        requiresEmailVerification,
        shopProfile,
        wholesalerId,
        userRole,
        getAuthToken,
        refreshToken,
        logout,
        loginWithGoogle,
        loginWithEmail,
        signupWithEmail,
        sendOTP,
        verifyOTP,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
